package org.gymly.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.util.userMessage
import org.gymly.util.downloadAuthenticatedFile
import org.gymly.util.downloadBytes
import org.gymly.util.looksLikeProofImage
import org.gymly.util.looksLikeProofPdf

/**
 * Vista de comprobante (imagen o PDF) para admin y portal.
 * Descarga con el cliente autenticado (Bearer).
 */
@Composable
fun ProofAttachmentView(
    proofUrl: String?,
    proofContentType: String? = null,
    label: String = "Comprobante",
    accent: Color,
    muted: Color,
    surface: Color,
    text: Color,
    outline: Color,
    compact: Boolean = false,
) {
    val url = proofUrl?.takeIf { it.isNotBlank() } ?: return
    val scope = rememberCoroutineScope()
    var busy by remember(url) { mutableStateOf(false) }
    var error by remember(url) { mutableStateOf<String?>(null) }
    var zoomOpen by remember(url) { mutableStateOf(false) }
    var imagenOk by remember(url) { mutableStateOf(false) }
    val showImage = looksLikeProofImage(proofContentType) && !looksLikeProofPdf(proofContentType)
    val isPdf = looksLikeProofPdf(proofContentType)

    fun openOrDownload() {
        if (busy) return
        scope.launch {
            busy = true
            error = null
            try {
                val file = downloadAuthenticatedFile(url, fallbackName = "comprobante")
                downloadBytes(file.fileName, file.mimeType, file.bytes)
            } catch (e: Exception) {
                error = e.userMessage("No se pudo abrir el comprobante.")
            } finally {
                busy = false
            }
        }
    }

    Column(Modifier.fillMaxWidth()) {
        // La imagen se carga en segundo plano. Mientras no esté lista, o si no se puede cargar,
        // no se muestra nada del comprobante: ni título ni recuadro vacío.
        if (showImage && !imagenOk) {
            AsyncImage(
                model = url,
                contentDescription = label,
                onSuccess = { imagenOk = true },
                onError = { imagenOk = false },
                modifier = Modifier.size(1.dp),
            )
            return@Column
        }
        Text(label, color = muted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
        Spacer(Modifier.height(6.dp))
        if (showImage) {
            AsyncImage(
                model = url,
                contentDescription = label,
                contentScale = ContentScale.Fit,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(if (compact) 140.dp else 200.dp)
                    .clip(RoundedCornerShape(10.dp))
                    .background(surface)
                    .border(1.dp, outline.copy(alpha = 0.55f), RoundedCornerShape(10.dp))
                    .clickable(enabled = !busy, onClick = { zoomOpen = true }),
            )
            Spacer(Modifier.height(4.dp))
            Text("Toca la imagen para ampliarla", color = muted, fontSize = 11.sp)
            Spacer(Modifier.height(6.dp))
        } else {
            Text(
                if (isPdf) "Archivo PDF adjunto" else "Archivo adjunto",
                color = text,
                fontSize = 14.sp,
                fontWeight = FontWeight.Medium,
            )
            Spacer(Modifier.height(4.dp))
        }
        TextButton(
            onClick = { openOrDownload() },
            enabled = !busy,
        ) {
            if (busy) {
                CircularProgressIndicator(Modifier.size(16.dp), color = accent, strokeWidth = 2.dp)
                Spacer(Modifier.size(8.dp))
            }
            Text(
                when {
                    busy -> "Abriendo…"
                    isPdf -> "Ver / descargar PDF"
                    else -> "Ver / descargar comprobante"
                },
                color = accent,
                fontWeight = FontWeight.SemiBold,
            )
        }
        error?.let {
            Text(it, color = Color(0xFFB3261E), fontSize = 12.sp, modifier = Modifier.padding(top = 4.dp))
        }
        if (zoomOpen && showImage) {
            ProofZoomDialog(imageUrl = url, title = label, onDismiss = { zoomOpen = false })
        }
    }
}
